// Typed API client for /v1/discoveries/*.
//
// Status enum matches backend JobStatusT (db.models._enums). The set of
// terminal states drives polling shutoff + cancel-button visibility, so
// keep it canonical here.

import { api } from '@/lib/api';

export type DiscoveryStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'canceled'
  | 'timeout';

export const TERMINAL_STATUSES: readonly DiscoveryStatus[] = [
  'succeeded',
  'failed',
  'canceled',
  'timeout',
];

export function isTerminal(status: DiscoveryStatus | undefined): boolean {
  return !!status && TERMINAL_STATUSES.includes(status);
}

export interface DiscoveryEdge {
  from: string;
  to: string;
  confidence: number;
  // Fraction of bootstrap resamples that agreed on the edge's ORIENTATION
  // (distinct from `confidence`, which is edge-PRESENCE stability). >=0.8 →
  // render a directed arrow; below (or null) → render an undirected line
  // because we found the relationship but can't defend its direction.
  // Null/undefined on legacy discoveries → treated as uncertain (see
  // DIRECTION_CONFIDENCE_THRESHOLD in the renderers).
  direction_confidence?: number | null;
  // Optional standardised regression coefficient ("β"). Backend does not
  // populate this yet — TODO(architect/backend-dev): thread the
  // linear-regression coefficient (or partial-correlation surrogate) into
  // each edge so the UI can render `94% · β=0.42` labels for trust.
  effect_size?: number;
  // Aliases the UI also checks before giving up, since different upstream
  // estimators name the same idea differently.
  weight?: number;
  coefficient?: number;
  // Provenance of the edge. `"data"` (default) = discovered by the
  // algorithm. `"knowledge_required"` / `"knowledge_oriented"` = asserted
  // or oriented via the user's domain knowledge — these count as CERTAIN in
  // the graph-confidence headline (edgeTrust = 1.0) unless the data
  // contradicts them. Absent on legacy payloads → treated as `"data"`.
  source?: 'data' | 'knowledge_required' | 'knowledge_oriented';
  // True when an edge the user asserted via knowledge is NOT supported by
  // the data. Such edges keep edgeTrust = e.confidence (not 1.0) and surface
  // an amber warning. Absent on legacy payloads → `false`.
  knowledge_conflict?: boolean;
}

export interface DiscoveryResult {
  schema_version: number;
  nodes: string[];
  edges: DiscoveryEdge[];
  adjacency_matrix?: number[][];
  dot?: string;
  metrics?: Record<string, unknown>;
}

export interface DiscoveryMetrics {
  n_nodes?: number;
  n_directed_edges?: number;
  n_undirected_edges?: number;
  is_dag?: boolean;
  processing_time_s?: number;
  [k: string]: unknown;
}

// AI-suggested domain knowledge (generated post-run from variable names +
// the discovered graph). Backend attaches this to the discovery whenever
// the LLM produced anything actionable; the card surfaces on every
// succeeded run (with or without an existing knowledge_id) so the user
// can refine. `null` = no suggestion (UI hides the card entirely).
//
// v2 contract: three independent sections (tiers, edges_required,
// edges_forbidden). Backend drops the field when ALL three are empty,
// but any one of them can be present in isolation. The legacy `kind`
// discriminator is kept on the type for backwards-compat with old rows
// already in the DB but is no longer consulted by the UI.
export interface SuggestedTier {
  label: string;
  variables: string[];
}

export interface SuggestedEdge {
  src: string;
  dst: string;
}

// Active-learning hint: an edge whose DIRECTION the data couldn't decide
// (Markov-equivalent / low orientation stability). Backend ranks up to 5 by
// how much confirming the direction would sharpen the rest of the graph.
// `from`/`to` are an arbitrary endpoint ordering — NOT a claimed direction;
// the UI renders them undirected (`from — to`) and lets the user orient.
export interface SuggestedLabel {
  from: string;
  to: string;
  presence: number;
  ambiguity_score: number;
  neighbors: number;
  reason: string;
}

export interface SuggestedKnowledge {
  // Legacy v1 discriminator. Some pre-2026-05-28 rows still carry
  // kind:'tiers' verbatim. v2 frontend ignores this field entirely and
  // dispatches off the three sub-arrays instead.
  kind?: string | null;
  rationale: string;
  tiers?: SuggestedTier[];
  edges_required?: SuggestedEdge[];
  edges_forbidden?: SuggestedEdge[];
}

export interface Discovery {
  id: string;
  dataset_id: string;
  // Human-friendly dataset label from the list endpoint (may be null on
  // single-GET). Used as the row's primary title in the discoveries list.
  dataset_name?: string | null;
  knowledge_id?: string | null;
  // Human-friendly knowledge label, populated by the LIST endpoint so the
  // list can render "SACHS · v2 (AI refinement)" instead of a raw kno_… id.
  knowledge_name?: string | null;
  status: DiscoveryStatus;
  algorithm_version: string;
  params: Record<string, unknown> & { seed?: number };
  input_fingerprint: string;
  error_code: string | null;
  error_message: string | null;
  queued_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  duration_ms?: number | null;
  // Live progress while the job is running. Frozen at the last seen value
  // once status becomes terminal. Phase labels mirror perfection_runner:
  // 'starting' | 'ges_running' | 'ges_done' | 'constraints' | 'cleanup' |
  // 'relaxation' | 'residual' | 'done'.
  progress_pct?: number;
  progress_phase?: string | null;
  // Number of OLDER queued+running jobs ahead of this one in the global
  // worker pool. Null on terminal status; 0 means "you're next or running".
  queue_position?: number | null;
  job_metrics?: DiscoveryMetrics | null;
  result?: DiscoveryResult | null;
  result_storage_key?: string | null;
  // AI plain-English explanation, generated once at completion. Null on
  // legacy rows → UI shows the on-demand "Explain this graph" button.
  narrative?: string | null;
  // Agent-generated label + one-line summary (shown in the discoveries list).
  // Null on legacy/agent-off rows → UI falls back to dataset name + counts.
  title?: string | null;
  summary?: string | null;
  // AI-proposed tier knowledge from variable names. Only emitted when the
  // discovery succeeded AND no knowledge_id was attached on the run. The UI
  // surface (SuggestedKnowledgeCard) validates the payload before rendering.
  suggested_knowledge?: SuggestedKnowledge | null;
  // Active-learning hints: up to 5 edges whose direction the data couldn't
  // decide, ranked by how much confirming them would sharpen the graph.
  // `null` when there's nothing to resolve (fully-directed graph or no edges).
  // Optional/nullable so older payloads without the field don't break.
  suggested_labels?: SuggestedLabel[] | null;
  // Single-number graph-trust metric, computed server-side from the result
  // edges (backend `_compute_graph_confidence` is the single source of truth;
  // GraphConfidenceCard reads this object directly, no client recompute).
  // Present on both LIST and single-GET for succeeded runs. Null when there's
  // no result / no numeric confidences.
  graph_confidence?: GraphConfidence | null;
  created_at: string;
}

// Server-computed trust headline for a finished discovery.
//
// HONEST confidence (2026-05-30 redesign): applying domain knowledge now
// RAISES the score. `score` is the working-graph headline — edges you
// confirmed count as certain (edgeTrust=1.0), others contribute their
// bootstrap stability. `score_unaided` is the algorithm-only number
// (data edges only), shown alongside so users can see what the data alone
// supported before their knowledge. Both are 0..100 integers.
export interface GraphConfidence {
  score: number;
  directed: number;
  total_edges: number;
  directed_ratio: number;
  // Algorithm-only score (data edges only). `null` when no knowledge was
  // applied (then `score` already is the algorithm-only number). Absent on
  // legacy payloads → treated as `null`.
  score_unaided?: number | null;
  // Count of edges asserted via the user's knowledge. Absent on legacy
  // payloads → treated as 0 (→ single-number, pre-redesign UI).
  knowledge_edges?: number;
  // Count of asserted edges the data contradicts. Absent → 0.
  conflicted_edges?: number;
}

export interface DiscoveryListResponse {
  items: Discovery[];
  next_cursor: string | null;
}

export interface CreateDiscoveryBody {
  dataset_id: string;
  // Optional sidecar — server validates ownership + (if pinned) dataset
  // match. Server-side fingerprint includes the KO payload so reruns with
  // different knowledge dedupe correctly.
  knowledge_id?: string | null;
  params?: { seed?: number };
}

export interface CreateDiscoveryOptions {
  idempotencyKey?: string;
  // Opt into the backend's sync-mode path. Backend will only honor it
  // when n_rows * n_cols^2 < 1e6 AND queue depth < 5.
  preferWait?: boolean;
}

export async function createDiscovery(
  body: CreateDiscoveryBody,
  options: CreateDiscoveryOptions = {},
): Promise<Discovery> {
  const headers: Record<string, string> = {};
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.preferWait) headers.Prefer = 'wait';
  const resp = await api.post<Discovery>('/discoveries', body, {
    headers,
    // Sync-mode wait budget is 12s server-side, but the fallback-to-async
    // path can take another ~10s of cleanup because the compute thread that
    // wait_for cancels keeps holding the GIL until it yields. Allow the
    // full 35s so the 408 has a chance to arrive instead of axios bailing.
    timeout: options.preferWait ? 35_000 : 30_000,
  });
  return resp.data;
}

// ETag cache for /discoveries/{id} polling.
//
// Backend emits a weak ETag of (status, updated_at). On a poll, we send
// If-None-Match; a 304 short-circuits with the cached body — 5× bandwidth
// win on long-running discoveries (the result blob is ~50KB and unchanged
// across most polls).
//
// Module-level cache (not Map-per-call) so cancelling a useDiscovery hook
// and remounting it still hits the cache. Bounded growth: an entry is
// only ever evicted by `clearDiscoveryEtagCache(id)` — but it's only one
// {etag,body} per discovery id, dwarfed by TanStack Query's own cache.
interface EtagCacheEntry {
  etag: string;
  body: Discovery;
}
const etagCache = new Map<string, EtagCacheEntry>();

export function clearDiscoveryEtagCache(id?: string): void {
  if (id === undefined) etagCache.clear();
  else etagCache.delete(id);
}

export async function getDiscovery(id: string): Promise<Discovery> {
  const cached = etagCache.get(id);
  const headers: Record<string, string> = {};
  if (cached) headers['If-None-Match'] = cached.etag;
  const resp = await api.get<Discovery>(`/discoveries/${id}`, {
    headers,
    // axios defaults to throw on non-2xx; we need 304 to flow through.
    validateStatus: (s) => s === 200 || s === 304,
  });
  if (resp.status === 304 && cached) {
    return cached.body;
  }
  const etagHeader = resp.headers?.etag ?? resp.headers?.ETag;
  if (typeof etagHeader === 'string' && etagHeader.length > 0) {
    etagCache.set(id, { etag: etagHeader, body: resp.data });
  }
  return resp.data;
}

export interface ListDiscoveriesParams {
  cursor?: string;
  limit?: number;
  // Single status OR an array for the multi-status path. Backend accepts a
  // comma-joined ``IN (...)`` filter so the "Active" tab can render
  // queued + running in one round-trip (no client-side merge dance).
  status?: DiscoveryStatus | readonly DiscoveryStatus[];
  dataset_id?: string;
}

export async function listDiscoveries(
  params: ListDiscoveriesParams = {},
): Promise<DiscoveryListResponse> {
  const { status, ...rest } = params;
  const queryParams: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== null) queryParams[k] = v as string | number;
  }
  if (status !== undefined) {
    queryParams.status = Array.isArray(status) ? status.join(',') : (status as string);
  }
  const resp = await api.get<DiscoveryListResponse>('/discoveries', {
    params: queryParams,
  });
  return resp.data;
}

export async function deleteDiscovery(id: string): Promise<void> {
  await api.delete(`/discoveries/${id}`);
}

export interface NarrativeResponse {
  narrative: string;
  cached: boolean;
}

/** Generate (or fetch cached) plain-English LLM explanation of the graph.
 * First call ~3s (Claude), subsequent calls instant (Redis-cached 7d).
 * Throws on 503 (feature off / generation failed) — caller shows fallback. */
export async function getDiscoveryNarrative(id: string): Promise<NarrativeResponse> {
  const resp = await api.post<NarrativeResponse>(`/discoveries/${id}/narrative`);
  return resp.data;
}

export async function cancelDiscovery(
  id: string,
  options: { idempotencyKey?: string } = {},
): Promise<Discovery> {
  const resp = await api.post<Discovery>(
    `/discoveries/${id}/cancel`,
    undefined,
    options.idempotencyKey ? ({ idempotencyKey: options.idempotencyKey } as never) : undefined,
  );
  // Cancel is a terminal transition — flush the ETag cache so the next
  // /discoveries/{id} GET re-syncs (otherwise we'd hand back a stale
  // "running" body for the rest of the polling interval).
  clearDiscoveryEtagCache(id);
  return resp.data;
}

// Trigger a browser download of a discovery result.
//
// Two-step pattern so cross-origin browser CORS doesn't bite us:
//  1. Backend returns JSON describing where the bytes are. Bearer is
//     attached by axios interceptor; same-origin → CORS easy.
//  2a. ``kind: "inline"`` → we already have the result dict. Save as Blob.
//  2b. ``kind: "s3"`` → presigned download_url has auth in its query
//      string. Do a top-level ``window.location.href`` navigation. No
//      Bearer-on-redirect, no CORS preflight against S3.
interface ResultEnvelope {
  kind: 'inline' | 's3';
  result?: Record<string, unknown>;
  download_url?: string;
  expires_in?: number;
}

// --------------------------------------------------------------------------
// Public-share endpoints (backend in progress 2026-05-22).
//
//   POST   /v1/discoveries/{id}/share   → { share_token, share_url }
//   DELETE /v1/discoveries/{id}/share   → 204
//   GET    /v1/share/{token}            → DiscoveryResult (anonymous, no JWT)
//
// The anonymous GET MUST NOT use the axios `api` instance — that interceptor
// attaches a Bearer token if one is present in memory, which would leak the
// signed-in user's identity to a public read. Use bare fetch + a recomputed
// absolute URL.
// --------------------------------------------------------------------------

export interface ShareLinkResponse {
  share_token: string;
  share_url: string;
}

export async function createShareLink(id: string): Promise<ShareLinkResponse> {
  const resp = await api.post<ShareLinkResponse>(`/discoveries/${id}/share`);
  return resp.data;
}

export async function deleteShareLink(id: string): Promise<void> {
  await api.delete(`/discoveries/${id}/share`);
}

// Read-only payload for the public /share/:token page. Mirrors the
// authenticated Discovery shape but drops every field the anonymous viewer
// has no business seeing (dataset_id, params, error envelopes, etc.).
export interface SharedDiscovery {
  id: string;
  algorithm_version: string;
  duration_ms?: number | null;
  result: DiscoveryResult;
  // AI plain-English summary — GraphView auto-renders it when present.
  narrative?: string | null;
  // Suggestion is anonymised on the shared view; the public viewer never
  // needs the "Apply & re-run" action so the type is kept identical purely
  // for parity with the authenticated payload.
  suggested_knowledge?: SuggestedKnowledge | null;
}

export async function getSharedDiscovery(token: string): Promise<SharedDiscovery> {
  // Reuse the same base URL the axios client is pointed at so prod / preview
  // / local dev all resolve the right host without hard-coding.
  const base = (api.defaults.baseURL ?? '/v1').replace(/\/$/, '');
  const resp = await fetch(`${base}/share/${encodeURIComponent(token)}`, {
    method: 'GET',
    credentials: 'omit',
    headers: { Accept: 'application/json' },
  });
  if (!resp.ok) {
    throw new Error(`Share link not found (HTTP ${resp.status})`);
  }
  return (await resp.json()) as SharedDiscovery;
}

export async function downloadDiscoveryResult(id: string): Promise<void> {
  const resp = await api.get<ResultEnvelope>(`/discoveries/${id}/result`);
  const body = resp.data;
  if (body.kind === 's3' && body.download_url) {
    window.location.href = body.download_url;
    return;
  }
  if (body.kind === 'inline' && body.result) {
    const blob = new Blob([JSON.stringify(body.result, null, 2)], {
      type: 'application/json',
    });
    const filename = `discovery-${id}.json`;
    const blobUrl = window.URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 0);
    }
    return;
  }
  throw new Error('Unexpected result envelope from server');
}
